import { useActivityLogParser, useMultiQuery } from 'features/activityLogs'
import { createRsrcKey } from 'features/resources'
import moment from 'moment'
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'
import useMultiSlice from 'Core/Hooks/useMultiSlice'

export const DashboardDataContext = createContext({
  heatMap: {
    users: {},
    devices: {},
    resources: {}
  },
  startDays: '1',
  dashboardReady: false,
  overallLoading: true, // Combined loading state
  setSetStartDays() {},
  transitionKey: 0 // Added transitionKey
})

export const DashboardDataProvider = ({ children }) => {
  const [startDays, setSetStartDays] = useState('1')
  const [transitionKey, setTransitionKey] = useState(0) // New state for transition

  // Effect to update transitionKey when startDays changes
  useEffect(() => {
    setTransitionKey((prevKey) => prevKey + 1)
  }, [startDays])
  const { startDateTime, endDateTime } = useMemo(
    () => ({
      startDateTime: moment().subtract(startDays, 'days'),
      endDateTime: new Date()
    }),
    [startDays]
  )
  const { slices, initiallyLoaded } = useMultiSlice(['userList', 'deviceList'])

  const { getQueryLogs, hits, fetching } = useMultiQuery({
    indexes: ['audit-log', 'proxy-audit-log'],
    startDateTime: startDateTime.toDate(),
    endDateTime: endDateTime
  })

  const { getParsedLogData } = useActivityLogParser()

  const heatMap = useMemo(() => {
    if (!initiallyLoaded || fetching) return { users: {}, devices: {}, resources: {} }

    const map = {
      users: {},
      devices: {},
      resources: {}
    }

    const parsedHits = getParsedLogData(hits)
    for (const hit of parsedHits) {
      const { user, device, resources, eventTime } = hit

      if (device) {
        const eventTimes = map.devices[createRsrcKey(device)]?.eventTimes || []

        map.devices[createRsrcKey(device)] = {
          count: (map.devices[createRsrcKey(device)]?.count || 0) + 1,
          device,
          eventTimes: [eventTime, ...eventTimes],
          owner: user
        }
      }

      if (user) {
        const eventTimes = map.users[createRsrcKey(user)]?.eventTimes || []
        map.users[createRsrcKey(user)] = {
          count: (map.users[createRsrcKey(user)]?.count || 0) + 1,
          user,
          eventTimes: [eventTime, ...eventTimes]
        }
      }

      if (resources?.length) {
        resources.forEach(({ key, rsrc }) => {
          if (!key || !rsrc) return
          const eventTimes = map.resources[key]?.eventTimes || []

          map.resources[key] = {
            count: (map.resources[key]?.count || 0) + 1,
            rsrc,
            eventTimes: [eventTime, ...eventTimes]
          }
        })
      }
    }
    return map
  }, [initiallyLoaded, fetching, hits, getParsedLogData])

  useEffect(() => {
    getQueryLogs()
  }, [startDays])

  const overallLoading = !initiallyLoaded || fetching

  const dashboardReady =
    initiallyLoaded &&
    !fetching &&
    (Object.keys(heatMap.users || {}).length > 0 || Object.keys(heatMap.resources || {}).length > 0)

  const value = useMemo(
    () => ({
      heatMap,
      setSetStartDays,
      startDays,
      overallLoading,
      dashboardReady,
      slices,
      initiallyLoaded,
      fetching,
      transitionKey
    }),
    [heatMap, startDays, overallLoading, dashboardReady, slices, initiallyLoaded, fetching]
  )

  return <DashboardDataContext.Provider value={value}>{children}</DashboardDataContext.Provider>
}

export const useDashboardDataProvider = () => useContext(DashboardDataContext)
